import React, { useEffect, useRef, useState } from 'react';
import { ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging';

import { CHAT_REGION } from '../../config';
import { fetchChatToken, fetchHistory, isUnauthorized } from '../../api';
import { buyersFromEvent, drawFromRecord, jackpotFromEvent, pad } from '../../draws';
import { OFFICIAL_NICKNAME, isOfficial } from '../../official';
import { productsLabel, purchaseFromEvent, upsertItem } from '../../purchases';
import { scoreboardFromEvent } from '../../scoreboard';
import { displayFromEvent } from '../../display';
import { stopwatchFromEvent } from '../../stopwatch';
import { MESSAGE_MAX, insertEmote, isEmotesOnly, splitEmotes } from '../../emotes';
import { EmotePicker, EmoteText } from './Emotes';

import './Chat.css';

const MAX_ITEMS = 1000; // what a phone keeps on screen: the history brings the last 200, the rest arrives live

const RECONNECT_MS = 1500; // after the room closes the connection from its side
// What the room sends is put on screen in batches this far apart, not one render per message:
// with everyone writing at once (dozens a second) a phone would spend itself re-drawing the list
export const FLUSH_MS = 120;
const NOTICE_MS = 6000; // how long "your message is in review" stays under the chat
// How long a draw notice ("Manuel ganó el Jackpot de clientes", "Salió el 212 en el
// jackpot") stays in the chat before it leaves on its own. The Sorteos tab keeps the result.
export const DRAW_NOTICE_MS = 120 * 1000;

const NICKNAME_COLORS = ['#f5c518', '#ff6b6b', '#4dabf7', '#69db7c', '#da77f2', '#ffa94d', '#38d9a9', '#f783ac'];

// Stable color per participant
const nicknameColor = (userId = '') => {
  let hash = 0;
  for (const ch of userId) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return NICKNAME_COLORS[Math.abs(hash) % NICKNAME_COLORS.length];
};

const toChatItem = (message) => ({
  type: 'MESSAGE',
  id: message.id,
  userId: message.sender.userId,
  nickname: message.sender.attributes?.nickname,
  content: message.content,
  sendTime: message.sendTime,
});

// With the review on, an approved message reaches the room as an `app:MESSAGE` event
const toApprovedMessage = ({ attributes }) => ({
  type: 'MESSAGE',
  id: attributes.id,
  userId: attributes.userId,
  nickname: attributes.nickname,
  content: attributes.content,
  sendTime: attributes.sendTime,
});

// The winner joins the chat once the reel has stopped, with the same id the history uses
const toWinnerItem = (draw, sendTime) => ({
  type: 'WINNER',
  id: draw.id,
  nickname: draw.winner,
  title: draw.title,
  total: draw.total,
  sendTime,
});

// The number joins the chat once the reels have stopped, with the same id the history uses
const toJackpotItem = (jackpot, sendTime) => ({
  type: 'JACKPOT',
  id: jackpot.id,
  number: jackpot.number,
  from: jackpot.from,
  to: jackpot.to,
  sendTime,
});

// Draw notices are the only items that leave the chat on their own
const isDrawNotice = (item) => item.type === 'WINNER' || item.type === 'JACKPOT';
const leavesAt = (item) => new Date(item.sendTime).getTime() + DRAW_NOTICE_MS;
const stillShown = (item, now = Date.now()) => !isDrawNotice(item) || leavesAt(item) > now;

// Merges two item lists, dropping duplicates by id and what already left, in chronological order
const mergeItems = (a, b) => {
  const byId = new Map();
  [...a, ...b].forEach((item) => byId.set(item.id, item));
  return [...byId.values()]
    .filter((item) => stillShown(item))
    .sort((x, y) => new Date(x.sendTime) - new Date(y.sendTime))
    .slice(-MAX_ITEMS);
};

// Appends a live item unless it already arrived with the history
const addItem = (items, item) =>
  items.some(({ id }) => id === item.id) ? items : [...items, item].slice(-MAX_ITEMS);

const MUTED_NOTICE = 'La moderación te sacó del chat. Podés seguir participando de los sorteos.';

// A line of the chat. Memoized: a new message draws itself, not the thousand before it
const ChatLine = React.memo(({ item, emotes }) => {
  const official = isOfficial(item.userId);
  return (
    <p className={`chat-line${isEmotesOnly(splitEmotes(item.content, emotes)) ? ' chat-line--emotes' : ''}`}>
      <span
        className={`chat-line__nick${official ? ' chat-line__nick--official' : ''}`}
        style={official ? undefined : { color: nicknameColor(item.userId) }}
      >
        {official ? OFFICIAL_NICKNAME : item.nickname}
      </span>
      {': '}
      <EmoteText content={item.content} emotes={emotes} />
    </p>
  );
});

// The card of a purchase, memoized like the lines
const PurchaseCard = React.memo(({ item }) => (
  <div className='purchase'>
    {item.photo ? (
      <img
        className='purchase__photo'
        src={item.photo}
        alt=''
        loading='lazy'
        referrerPolicy='no-referrer'
        onError={(e) => {
          e.currentTarget.hidden = true; // photo that does not load leaves the card intact
        }}
      />
    ) : (
      <span className='purchase__icon' aria-hidden='true'>🛒</span>
    )}
    <div className='purchase__body'>
      <p className='purchase__text'>
        <strong>{item.nickname}</strong> hizo una compra
      </p>
      {item.products?.length > 0 && <p className='purchase__products'>{productsLabel(item.products)}</p>}
    </div>
  </div>
));

const Chat = ({ session, onSessionExpired, onDraw, onScoreboard, onDisplay = () => {}, onStopwatch = () => {}, onStatus, emotes = [], hidden }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState(null);
  const [mutedByModeration, setMutedByModeration] = useState(false);
  const roomRef = useRef(null);
  const reconnectTimer = useRef(null);
  const noticeTimer = useRef(null);
  const inputRef = useRef(null);

  // A line under the chat that clears itself
  const notify = (text) => {
    setSendError(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setSendError(null), NOTICE_MS);
  };
  const listRef = useRef(null);
  const stickToBottom = useRef(true);
  const winnerTimers = useRef([]);
  const updates = useRef([]);
  const flushTimer = useRef(null);

  // Everything the room changes in the list goes through here, in the order it arrived, and
  // reaches the screen in one render per batch
  const updateItems = (update) => {
    updates.current.push(update);
    if (flushTimer.current) return;
    flushTimer.current = setTimeout(() => {
      flushTimer.current = null;
      const batch = updates.current;
      updates.current = [];
      setItems((prev) => batch.reduce((items, apply) => apply(items), prev));
    }, FLUSH_MS);
  };

  const { sessionToken, participant, returning } = session;

  useEffect(() => {
    const room = new ChatRoom({
      regionOrUrl: CHAT_REGION,
      tokenProvider: async () => {
        try {
          const data = await fetchChatToken(sessionToken);
          // Muted: the token only watches. Every token says it again, so unmuting shows too
          setMutedByModeration(Boolean(data.muted));
          return data;
        } catch (err) {
          if (isUnauthorized(err)) onSessionExpired();
          throw err;
        }
      },
    });
    room.logLevel = 'error'; // the SDK logs every event to the console otherwise: noise, and work, with a busy room
    roomRef.current = room;

    const unsubscribers = [
      room.addListener('connecting', () => setStatus('connecting')),
      room.addListener('connect', () => {
        setStatus('connected');
        // History is loaded after connecting (and on every reconnect) so nothing sent in between is lost
        fetchHistory(sessionToken)
          .then(({ items: history, lastDraw, scoreboard: board, display, stopwatch }) => {
            updateItems((live) => mergeItems(history, live));
            // Whoever joins after a draw still sees its result on the stage tab
            if (lastDraw) onDraw(drawFromRecord(lastDraw));
            // And the scoreboard as it stands, with whatever is left of the timer
            onScoreboard(board);
            // And what the panel left showing: the scoreboard and the draws can be hidden
            onDisplay(display);
            // And the stopwatch of the stage, with what it has counted so far
            onStopwatch(stopwatch);
          })
          .catch((err) => {
            if (isUnauthorized(err)) onSessionExpired();
          });
      }),
      room.addListener('disconnect', (reason) => {
        setStatus('disconnected');
        // Closed from the room's side: the moderation muted or unmuted us, or the session
        // ran out. Back in with a fresh token, which carries the right permissions
        if (reason === 'serverDisconnect') {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = setTimeout(() => room.connect(), RECONNECT_MS);
        }
      }),
      room.addListener('message', (message) => updateItems((prev) => addItem(prev, toChatItem(message)))),
      room.addListener('event', (event) => {
        if (event.eventName === 'app:PURCHASE') {
          // Sent again when the panel takes the photo off: the card is replaced in place
          updateItems((prev) => upsertItem(prev, purchaseFromEvent(event), MAX_ITEMS));
        } else if (event.eventName === 'app:DELETE') {
          // The panel took a purchase down (a message is deleted in IVS itself: see messageDelete)
          updateItems((prev) => prev.filter((item) => item.id !== event.attributes.id));
        } else if (event.eventName === 'app:CLEAR') {
          // The panel cleaned up for the next event: the chat starts empty, or everyone was
          // taken out and this session is gone with them, so it is back to the registration
          if (event.attributes.scope === 'chat') updateItems(() => []);
          if (event.attributes.scope === 'users') onSessionExpired();
        } else if (event.eventName === 'app:MESSAGE') {
          updateItems((prev) => addItem(prev, toApprovedMessage(event)));
          // Our own message got published: nothing is in review any more
          if (event.attributes.userId === participant.id) {
            clearTimeout(noticeTimer.current);
            setSendError(null);
          }
        } else if (event.eventName === 'app:BUYERS') {
          // The room owns the connection, so the draw is lifted to the app and the stage starts everywhere
          const draw = buyersFromEvent(event);
          onDraw(draw);
          winnerTimers.current.push(
            setTimeout(() => updateItems((prev) => addItem(prev, toWinnerItem(draw, new Date()))), draw.duration)
          );
        } else if (event.eventName === 'app:JACKPOT') {
          const jackpot = jackpotFromEvent(event);
          onDraw(jackpot);
          winnerTimers.current.push(
            setTimeout(() => updateItems((prev) => addItem(prev, toJackpotItem(jackpot, new Date()))), jackpot.duration)
          );
        } else if (event.eventName === 'app:SCORE') {
          // A team got points, or the timer of the game started or ended: lifted to the app like the draws
          onScoreboard(scoreboardFromEvent(event));
        } else if (event.eventName === 'app:DISPLAY') {
          // The panel hid the scoreboard or the draws from the phones, or brought them back
          onDisplay(displayFromEvent(event));
        } else if (event.eventName === 'app:STOPWATCH') {
          // The panel started, paused or reset the stopwatch
          onStopwatch(stopwatchFromEvent(event));
        }
      }),
      room.addListener('messageDelete', ({ messageId }) =>
        updateItems((prev) => prev.filter((item) => item.id !== messageId))
      ),
    ];

    room.connect();

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      winnerTimers.current.forEach(clearTimeout);
      winnerTimers.current = [];
      clearTimeout(reconnectTimer.current);
      clearTimeout(noticeTimer.current);
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
      updates.current = [];
      room.disconnect();
    };
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // The bar of the app shows how the connection is going, and offers to get back in
  useEffect(() => {
    onStatus(status, () => roomRef.current?.connect());
  }, [status, onStatus]);

  // Draw notices leave on their own: one timer, for the next one due
  useEffect(() => {
    const due = items.filter(isDrawNotice).map(leavesAt);
    if (!due.length) return undefined;
    const timer = setTimeout(
      () => setItems((prev) => prev.filter((item) => stillShown(item))),
      Math.max(0, Math.min(...due) - Date.now())
    );
    return () => clearTimeout(timer);
  }, [items]);

  // Keep the list pinned to the newest message unless the user scrolled up to read
  useEffect(() => {
    const list = listRef.current;
    if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
  }, [items, hidden]);

  const handleScroll = () => {
    const list = listRef.current;
    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || status !== 'connected') return;

    setDraft('');
    stickToBottom.current = true;

    // The room answers nothing for a held message: it shows up when the panel publishes it
    // or on its own once the review window is over. Only sending too fast comes back
    notify('Tu mensaje está en revisión');
    roomRef.current.sendMessage(new SendMessageRequest(content)).catch((err) => {
      if (err?.errorCode === 429) {
        setDraft(content);
        notify('Estás enviando mensajes muy rápido, esperá un momento');
      }
    });
  };

  // An emote from the picker lands where the cursor was, and the keyboard comes back
  const pickEmote = (code) => {
    const input = inputRef.current;
    const { text, cursor } = insertEmote(draft, code, input?.selectionStart ?? draft.length);
    setDraft(text);
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(cursor, cursor);
    });
  };

  const connected = status === 'connected' && !mutedByModeration;

  return (
    <section className='chat' hidden={hidden}>
      <div className='chat__messages' ref={listRef} onScroll={handleScroll}>
        <p className='chat__notice'>
          {returning ? `¡Hola de nuevo, ${participant.nickname}!` : `¡Bienvenido/a, ${participant.nickname}!`}
        </p>
        {items.map((item) => {
          if (item.type === 'WINNER') {
            return (
              <div key={item.id} className='winner'>
                <span className='winner__icon' aria-hidden='true'>🎉</span>
                <p className='winner__text'>
                  <strong>{item.nickname}</strong> ganó el {item.title || 'jackpot de clientes'}
                </p>
              </div>
            );
          }
          if (item.type === 'JACKPOT') {
            return (
              <div key={item.id} className='winner'>
                <span className='winner__icon' aria-hidden='true'>🎰</span>
                <p className='winner__text'>
                  Salió el <strong>{pad(item.number)}</strong> en el jackpot
                </p>
              </div>
            );
          }

          return item.type === 'PURCHASE' ? (
            <PurchaseCard key={item.id} item={item} />
          ) : (
            <ChatLine key={item.id} item={item} emotes={emotes} />
          );
        })}
      </div>

      {sendError && <p className='chat__error'>{sendError}</p>}

      {mutedByModeration ? (
        <p className='chat__muted'>{MUTED_NOTICE}</p>
      ) : (
      <form className='composer' onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className='input'
          type='text'
          placeholder={connected ? 'Escribí un mensaje…' : 'Conectando…'}
          maxLength={MESSAGE_MAX}
          value={draft}
          disabled={!connected}
          onChange={(e) => setDraft(e.target.value)}
        />
        <EmotePicker emotes={emotes} disabled={!connected} onPick={pickEmote} />
        <button type='submit' className='btn-primary' disabled={!connected || !draft.trim()}>
          Enviar
        </button>
      </form>
      )}
    </section>
  );
};

export default Chat;
